import { useState, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button } from '@/components/Button/Button';
import { Chip } from '@/components/Chip/Chip';
import { t } from '@/lib/i18n';
import { formatPersonName } from '@/lib/format';
import type { AddWizardValues, MatchResponse } from '../../schemas';
import {
  useMatchStaffProfiles,
  usePositionsCatalog,
  useStaffProfiles,
  useUnitsCatalog,
} from '../../hooks';
import styles from './StepRole.module.css';

const SUGGESTIONS = [
  'I need someone to write our launch announcements and weekly newsletter',
  'Someone to close the books each month and flag weird transactions',
  'An assistant who can handle my inbox and protect my calendar',
  'Someone who can scan the competitive landscape every Monday',
];

export function StepRole() {
  const { watch, setValue, register } = useFormContext<AddWizardValues>();
  const description = watch('description');
  const selectedId = watch('staffProfileId');
  const skipped = watch('roleSkipped');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const matcher = useMatchStaffProfiles();
  const profiles = useStaffProfiles();
  const positions = usePositionsCatalog();
  const units = useUnitsCatalog();

  const [results, setResults] = useState<MatchResponse['matches'] | null>(null);
  const [error, setError] = useState<string | null>(null);

  function runMatcher() {
    const trimmed = description.trim();
    if (trimmed.length === 0) return;
    setError(null);
    matcher.mutate(
      { description: trimmed },
      {
        onSuccess: (data) => setResults(data.matches),
        onError: () => setError(t("Couldn't find positions right now. Try again.")),
      },
    );
  }

  function pickSuggestion(text: string) {
    setValue('description', text, { shouldValidate: true });
    setResults(null);
    setValue('staffProfileId', '');
    setValue('roleSkipped', false, { shouldValidate: true });
    textareaRef.current?.focus();
  }

  function selectResult(id: string) {
    setValue('staffProfileId', id, { shouldValidate: true });
    setValue('roleSkipped', false, { shouldValidate: true });
  }

  function skipStep() {
    setValue('staffProfileId', '');
    setValue('roleSkipped', true, { shouldValidate: true });
  }

  function profileFor(id: string) {
    return profiles.data?.items.find((p) => p.id === id);
  }

  function positionNameFor(id: string | null | undefined) {
    return id ? positions.data?.items.find((p) => p.id === id)?.name : undefined;
  }

  function unitNameFor(id: string | null | undefined) {
    return id ? units.data?.items.find((u) => u.id === id)?.name : undefined;
  }

  const reg = register('description');

  return (
    <section data-testid="step-role">
      <div className={styles.header}>
        <div className={styles.stepLabel}>{t('STEP 2')}</div>
        <h1 className={styles.title}>{t("Describe the position you're hiring for.")}</h1>
        <p className={styles.subtitle}>
          {t(
            "Plain English. Tell us what kind of work this Member will own. We'll find positions that fit.",
          )}
        </p>
      </div>

      <div className={styles.promptCard}>
        <textarea
          {...reg}
          ref={(el) => {
            reg.ref(el);
            textareaRef.current = el;
          }}
          className={styles.textarea}
          placeholder={t(
            'e.g. "Someone who can write our launch announcements and a weekly newsletter"',
          )}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              runMatcher();
            }
          }}
        />
        <div className={styles.promptFooter}>
          <Button
            type="button"
            variant="primary"
            size="md"
            isLoading={matcher.isPending}
            onClick={runMatcher}
            disabled={description.trim().length === 0}
          >
            {results !== null ? t('Try again') : t('Find matching positions')}
          </Button>
        </div>
      </div>

      {error ? (
        <div className={styles.error} role="alert">
          {error}
        </div>
      ) : null}

      {matcher.isPending ? (
        <>
          <div className={styles.resultsLabel} aria-live="polite">
            {t('FINDING POSITIONS…')}
          </div>
          <div className={styles.results} aria-busy="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className={styles.resultSkeleton} aria-hidden="true">
                <span className={styles.skelRank} />
                <span className={styles.skelBody}>
                  <span className={styles.skelLineLg} />
                  <span className={styles.skelLineSm} />
                </span>
                <span className={styles.skelDot} />
              </div>
            ))}
          </div>
        </>
      ) : results === null ? (
        <>
          <div className={styles.suggestionsLabel}>{t('OR TRY ONE OF THESE')}</div>
          <div className={styles.suggestions}>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                className={styles.suggestion}
                onClick={() => pickSuggestion(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </>
      ) : results.length === 0 ? (
        <div className={styles.empty} role="status">
          {t('No positions fit yet — try rephrasing.')}
        </div>
      ) : (
        <>
          <div className={styles.resultsLabel}>{t('BEST MATCHES')}</div>
          <div className={styles.resultsTitle}>
            {results.length === 1
              ? t('Found 1 position that fits')
              : t('Found {n} positions that fit', { n: results.length })}
          </div>
          <div className={styles.results}>
            {results.map((m, idx) => {
              const profile = profileFor(m.staffProfileId);
              const checked = selectedId === m.staffProfileId;
              const profileName = profile ? formatPersonName(profile) : t('Staff Profile');
              // Cards show the alphabetically-first default as the headline
              // label; the detail page surfaces the full set.
              const positionName = positionNameFor(profile?.defaultPositionIds[0]);
              const unitName = unitNameFor(profile?.defaultUnitIds[0]);
              return (
                <button
                  key={m.staffProfileId}
                  type="button"
                  className={[styles.result, checked ? styles.resultChecked : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => selectResult(m.staffProfileId)}
                  aria-pressed={checked}
                >
                  <span
                    className={[styles.rank, idx === 0 ? '' : styles.rankFaded]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {idx + 1}
                  </span>
                  <span className={styles.resultBody}>
                    <span className={styles.resultName}>{profileName}</span>
                    {positionName ? (
                      <span className={styles.resultSubtitle}>{positionName}</span>
                    ) : null}
                    {unitName ? (
                      <span className={styles.resultChips}>
                        <Chip variant="neutral" size="sm">
                          {unitName}
                        </Chip>
                      </span>
                    ) : null}
                    {profile?.description ? (
                      <span className={styles.resultDesc}>{profile.description}</span>
                    ) : null}
                    <span className={styles.resultJustification}>
                      &ldquo;{m.justification}&rdquo;
                    </span>
                  </span>
                  <span
                    className={[styles.radioDot, checked ? styles.radioDotChecked : '']
                      .filter(Boolean)
                      .join(' ')}
                  />
                </button>
              );
            })}
          </div>
        </>
      )}

      <div className={styles.skipRow}>
        {skipped ? (
          <span className={styles.skipActive} role="status">
            {t("Skipping this step — you'll write everything from scratch.")}{' '}
            <button
              type="button"
              className={styles.skipUndo}
              onClick={() => setValue('roleSkipped', false, { shouldValidate: true })}
            >
              {t('Undo')}
            </button>
          </span>
        ) : (
          <button type="button" className={styles.skipLink} onClick={skipStep}>
            {t('Skip — I’ll write everything from scratch →')}
          </button>
        )}
      </div>
    </section>
  );
}
